import React, { useCallback, useEffect, useState } from 'react';

import { ExclamationCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Drawer as DrawerAntD, Button, Input, message, Modal, Result, Select, Table, Tabs, Tag } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import { ApiDocumentation } from './ApiDocumentation';
import {
  clampPage,
  EMPTY_INTEGRATION_FILTER,
  filterIntegrationTokens,
  IntegrationTokenFilter,
  pageOf,
} from './integrationFilters';
import {
  createdLabel,
  INTEGRATION_SCOPES,
  isSensitiveScope,
  lastUseLabel,
  scopeLabel,
  tokenStatus,
} from './integrationLabels';
import { IntegrationTokenCreatedModal } from './IntegrationTokenCreatedModal';
import { IntegrationTokenCreateModal } from './IntegrationTokenCreateModal';
import { buildPostmanCollection, postmanFileName } from './postmanCollection';


import { integrationApiBaseUrl ,
  deleteIntegrationToken,
  extendIntegrationTokenValidity,
  getIntegrationTokens,
  recreateIntegrationToken,
  renewIntegrationToken,
  revokeIntegrationToken,
} from '../../../../hooks/interoperability';
import {
  IntegrationToken,
  IntegrationTokenCreated,
} from '../../../../types/responses/Interoperability';
import StatusBadge from '../../../atoms/StatusBadge';
import { IntegrationTokenDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { ListSkeleton } from '../../../molecules/ListSkeleton/ListSkeleton';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { Chevron, FilterBar, FilterCount, FilterSpacer, PrimaryButton, SecondaryButton } from '../styled';

// O antd 4.10 nao tipa bem o Drawer com styled-components; o financeiro faz o mesmo alias.
const Drawer: any = DrawerAntD;

// Sub-abas da aba API: gestão de tokens e documentação, separadas para não empilhar tudo numa
// tela só. Estado local (não vai para a URL): o endereço do módulo já aponta a aba API.
type SubTab = 'tokens' | 'docs';

const Card = styled.div`
  background: #fff;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 12px;
`;

/** Mesmo agrupamento das ações do cabeçalho da página, para o botão cair no mesmo lugar. */
const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex: none;
`;

const Intro = styled.p`
  flex: 1;
  margin: 0;
  color: #5b6472;
  font-size: 13px;
  line-height: 1.6;
  max-width: 720px;
`;

const Name = styled.div`
  color: #12263f;
  font-size: 13px;
`;

const Hint = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  color: #5b6472;
  white-space: nowrap;
`;

const Unknown = styled.span`
  color: #8d929e;
  font-size: 12px;
`;

const Sub = styled.div`
  color: #8d929e;
  font-size: 12px;
  margin-top: 2px;
`;

const Foot = styled.div`
  border-top: 1px solid #e9ebee;
  background: #fafbfc;
  padding: 10px 16px;
  font-size: 12px;
  color: #6b7280;
`;

/** Mesmo tamanho de página das outras listagens do módulo. */
const PAGE_SIZE = 25;

export type IntegrationTokensTabProps = {
  tenantId: string;

  /** Nome da conta, para nomear a coleção do Postman. */
  accountName?: string;
};

/**
 * Tokens de integração da conta.
 *
 * É a credencial que a conta entrega a um integrador para ele operar em nome dela. Diferente
 * do token da conta (aba principal), que só permite ser convidada: este opera.
 *
 * O valor em claro aparece uma única vez, logo após criar, e nunca mais — nem aqui, nem em
 * nenhum outro caminho da interface.
 */
export const IntegrationTokensTab: React.FC<IntegrationTokensTabProps> = ({
  tenantId,
  accountName = '',
}) => {
  const [subTab, setSubTab] = useState<SubTab>('docs');
  const [tokens, setTokens] = useState<IntegrationToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<IntegrationTokenCreated | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [opened, setOpened] = useState<IntegrationToken | null>(null);
  const [filter, setFilter] = useState<IntegrationTokenFilter>(EMPTY_INTEGRATION_FILTER);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      setTokens((await getIntegrationTokens(tenantId)) || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Revogar, com confirmação que diz o efeito.
   *
   * Irreversível: quem revoga precisa saber que a integração para de funcionar na hora, e
   * que voltar exige criar outro token e reconfigurar o lado do integrador.
   */
  const confirmDelete = (token: IntegrationToken) => {
    Modal.confirm({
      title: T('Delete this revoked token?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('The token')} ${token.name} ${TF(
        'is removed for good. This cannot be undone; the audit trail keeps the record.',
      )}`,
      okText: T('Delete'),
      okButtonProps: { danger: true },
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(token.token_id);
          await deleteIntegrationToken(tenantId, token.token_id);
          setOpened(null);
          message.success(T('Token deleted.'));
          await load();
        } catch {
          message.error(T('Could not delete the token. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const confirmRevoke = (token: IntegrationToken) => {
    Modal.confirm({
      title: T('Revoke this token?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('The integration of')} ${token.name} ${TF(
        'stops working immediately. To restore it you have to create another token and configure it on their side.',
      )}`,
      okText: T('Revoke'),
      okButtonProps: { danger: true },
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(token.token_id);
          await revokeIntegrationToken(tenantId, token.token_id);
          setOpened(null);
          message.success(T('Token revoked.'));
          await load();
        } catch {
          message.error(T('Could not revoke the token. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /**
   * Renovar: troca o segredo e mostra o novo uma vez.
   *
   * A confirmação existe porque a integração para de funcionar no instante da troca, até
   * alguém configurar a chave nova do outro lado.
   */
  const confirmRenew = (token: IntegrationToken) => {
    Modal.confirm({
      title: T('Renew this token?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('The current secret stops working immediately and')} ${token.name} ${TF(
        'will need the new one. Name and scopes stay the same.',
      )}`,
      okText: T('Renew'),
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(token.token_id);
          setCreated(await renewIntegrationToken(tenantId, token.token_id));
          setOpened(null);
          message.success(T('Token renewed.'));
          await load();
        } catch {
          message.error(T('Could not renew the token. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /**
   * Estender: mesmo segredo, prazo novo.
   *
   * Sem pedir data: o caso de uso é destravar uma integração que venceu, e escolher data
   * aqui seria fricção para uma decisão que quase sempre é "volte a valer". Quem quer prazo
   * específico renova e escolhe na criação.
   */
  const confirmExtend = (token: IntegrationToken) => {
    Modal.confirm({
      title: T('Extend this token?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('The same secret goes back to working, with no expiry date.')} ${TF(
        'It was never compromised — it only aged out.',
      )}`,
      okText: T('Extend validity'),
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(token.token_id);
          await extendIntegrationTokenValidity(tenantId, token.token_id, null);
          setOpened(null);
          message.success(T('Validity extended.'));
          await load();
        } catch {
          message.error(T('Could not extend the validity. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /**
   * Recriar: token novo com a mesma configuração.
   *
   * O revogado continua revogado na lista. É de propósito: a trilha de quem teve acesso a
   * quê não se reescreve.
   */
  const confirmRecreate = (token: IntegrationToken) => {
    Modal.confirm({
      title: T('Create a new token like this one?'),
      icon: <ExclamationCircleOutlined />,
      content: `${TF('A new token is created with the same name and scopes.')} ${TF(
        'The revoked one stays revoked in the list — the old secret never comes back.',
      )}`,
      okText: T('Recreate'),
      cancelText: T('Cancel'),
      onOk: async () => {
        try {
          setBusyId(token.token_id);
          setCreated(await recreateIntegrationToken(tenantId, token.token_id));
          setOpened(null);
          message.success(T('Token recreated.'));
          await load();
        } catch {
          message.error(T('Could not recreate the token. Nothing was changed.'));
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  /*
    Filtro e paginação no cliente, e isso é correto e não atalho: o endpoint devolve todos os
    tokens da conta. Com o conjunto completo em mãos, o resultado é o mesmo que faria no
    servidor. Se a listagem virar paginada no servidor, os filtros têm de ir com ela.
  */
  const filtered = filterIntegrationTokens(tokens, filter);
  const currentPage = clampPage(page, filtered.length, PAGE_SIZE);
  const visible = pageOf(filtered, currentPage, PAGE_SIZE);

  /** Trocar filtro volta para a primeira página: a atual pode não existir no resultado. */
  const changeFilter = (change: Partial<IntegrationTokenFilter>) => {
    setFilter((current) => ({ ...current, ...change }));
    setPage(1);
  };

  /**
   * Baixa a coleção do Postman.
   *
   * Montada no cliente e entregue como arquivo: nada sai para servidor externo, e a coleção
   * não carrega token — ele entra como variável vazia, para quem importar preencher.
   */
  const downloadPostman = () => {
    const collection = buildPostmanCollection(integrationApiBaseUrl(), accountName);

    const file = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(file);

    const link = document.createElement('a');
    link.href = url;
    link.download = postmanFileName(accountName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Sem revogar, o blob fica na memória da aba até ela fechar.
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: T('Integrator'),
      key: 'name',
      render: (_: unknown, token: IntegrationToken) => (
        <div>
          <Name>{token.name}</Name>
          {token.contact_email && <Sub>{token.contact_email}</Sub>}
          {!token.contact_email && token.created_by && <Sub>{token.created_by}</Sub>}
        </div>
      ),
    },
    {
      /*
        Identificação parcial. O valor completo não existe em lugar nenhum além da resposta
        da criação ou da renovação — o banco guarda hash. Estes quatro caracteres respondem
        "qual token é este?" sem que nada recuperável seja guardado.
      */
      title: T('Token'),
      key: 'hint',
      render: (_: unknown, token: IntegrationToken) =>
        token.token_hint ? (
          <Hint>move_key_…{token.token_hint}</Hint>
        ) : (
          // Token criado antes de o campo existir: honesto dizer que não se sabe.
          <Unknown>{T('not identified')}</Unknown>
        ),
    },
    {
      title: T('Scopes'),
      key: 'scopes',
      render: (_: unknown, token: IntegrationToken) => (
        <>
          {token.scopes.map((scope) => (
            <Tag key={scope} color={isSensitiveScope(scope) ? 'orange' : undefined}>
              {scopeLabel(scope)}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: T('Created'),
      key: 'created',
      render: (_: unknown, token: IntegrationToken) => (
        <div>
          {createdLabel(token)}
          {/* Só aparece quando houve troca de segredo: created_at é o nascimento da
              integração, renewed_at é a última chave. */}
          {token.renewed_at && (
            <Sub>
              {T('renewed on')} {new Date(token.renewed_at).toLocaleString()}
            </Sub>
          )}
        </div>
      ),
    },
    {
      title: T('Last use'),
      key: 'lastUse',
      render: (_: unknown, token: IntegrationToken) => (
        <span style={{ color: token.last_used_at ? undefined : '#8d929e' }}>
          {lastUseLabel(token)}
        </span>
      ),
    },
    {
      title: T('Status'),
      key: 'status',
      render: (_: unknown, token: IntegrationToken) => {
        const status = tokenStatus(token);

        return (
          <StatusBadge tone={status}>
            {status.label}
          </StatusBadge>
        );
      },
    },
    {
      /*
        Chevron e detalhe na lateral, como o financeiro e a aba Conexoes. As acoes vivem no
        detalhe: elas dependem do estado e precisam de uma frase para nao serem confundidas
        — renovar troca o segredo, recriar nasce outro token, estender mantem o mesmo.
      */
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  return (
    <div>
      <Tabs activeKey={subTab} onChange={(key) => setSubTab(key as SubTab)}>
        <Tabs.TabPane tab={T('Documentation')} key="docs" forceRender>
          <Header>
            <Intro>
              {T(
                'How your integrators authenticate and what the API offers. The Postman collection carries the same endpoints.',
              )}
            </Intro>
            <Actions>
              <SecondaryButton type="button" onClick={downloadPostman}>
                {T('Download Postman collection')}
              </SecondaryButton>
            </Actions>
          </Header>

          <ApiDocumentation baseUrl={integrationApiBaseUrl()} />
        </Tabs.TabPane>

        <Tabs.TabPane tab={T('Tokens')} key="tokens" forceRender>
          <Header>
            <Intro>
              {T(
                'Tokens your integrators use to operate in your name. Each one carries only the scopes you grant, is shown once at creation, and can be revoked at any time.',
              )}
            </Intro>
            <Actions>
              <PrimaryButton type="button" onClick={() => setCreating(true)}>
                {T('New token')}
              </PrimaryButton>
            </Actions>
          </Header>

      {loadError && (
        <Result
          status="warning"
          title={T('Could not load the integration tokens.')}
          extra={
            <Button type="primary" onClick={load}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {loading && <ListSkeleton />}

      {!loading && !loadError && (
        <FilterBar>
          <Input.Search
            allowClear
            style={{ maxWidth: 260 }}
            placeholder={TF('Search integrator, e-mail or token')}
            value={filter.search}
            onChange={(event) => changeFilter({ search: event.target.value })}
          />

          <Select
            value={filter.state}
            style={{ minWidth: 160 }}
            onChange={(state: IntegrationTokenFilter['state']) => changeFilter({ state })}
          >
            <Select.Option value="ALL">{T('All states')}</Select.Option>
            <Select.Option value="ACTIVE">{T('Active')}</Select.Option>
            <Select.Option value="EXPIRED">{T('Expired')}</Select.Option>
            <Select.Option value="REVOKED">{T('Revoked')}</Select.Option>
          </Select>

          <Select
            value={filter.scope}
            style={{ minWidth: 190 }}
            onChange={(scope: IntegrationTokenFilter['scope']) => changeFilter({ scope })}
          >
            <Select.Option value="">{T('Any scope')}</Select.Option>
            {INTEGRATION_SCOPES.map((scope) => (
              <Select.Option key={scope} value={scope}>
                {scopeLabel(scope)}
              </Select.Option>
            ))}
          </Select>

          <FilterSpacer />

          {/* Contagem porque filtro sem ela esconde o próprio efeito: pouca linha na tela
              pode ser pouca coisa, ou corte do filtro. */}
          <FilterCount>
            {filtered.length === tokens.length
              ? `${tokens.length} ${TF('tokens')}`
              : `${filtered.length} ${TF('of')} ${tokens.length} ${TF('tokens')}`}
          </FilterCount>
        </FilterBar>
      )}

      {!loading && !loadError && (
        <Card>
          <Table
            rowKey="token_id"
            locale={{
              // Nenhum resultado por filtro é diferente de nenhum token: quem filtrou precisa
              // saber que existe coisa fora do filtro.
              emptyText: filtered.length === tokens.length
                ? T('No integration tokens yet. Create one to give an integrator access.')
                : T('No token matches these filters.'),
            }}
            pagination={false}
            columns={columns}
            dataSource={visible}
            scroll={{ x: true }}
            onRow={(token: IntegrationToken) => ({
              onClick: () => setOpened(token),
              style: { cursor: 'pointer' },
            })}
          />

          <Foot>
            {T('A token is never shown again after creation — if one is lost, revoke it and create another.')}
          </Foot>

          {/* Paginador do dashboard, 1-based como ele espera. */}
          <Pagination
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={filtered.length}
            onPageChange={(next: number) => setPage(next)}
          />
        </Card>
      )}

        </Tabs.TabPane>
      </Tabs>

      {creating && (
        <IntegrationTokenCreateModal
          tenantId={tenantId}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            load();
          }}
        />
      )}

      {created && (
        <IntegrationTokenCreatedModal created={created} onClose={() => setCreated(null)} />
      )}

      <Drawer
        visible={!!opened}
        width={480}
        title={opened?.name}
        onClose={() => setOpened(null)}
        destroyOnClose
      >
        {opened && (
          <IntegrationTokenDrawerDetails
            token={opened}
            busy={busyId === opened.token_id}
            onRenew={() => confirmRenew(opened)}
            onRevoke={() => confirmRevoke(opened)}
            onExtend={() => confirmExtend(opened)}
            onRecreate={() => confirmRecreate(opened)}
            onDelete={() => confirmDelete(opened)}
          />
        )}
      </Drawer>
    </div>
  );
};
